import { useCallback, useEffect, useMemo, useState } from 'react'
import siteMap from '../../../siteMap'
import { useSelector } from 'react-redux'
import { RootState } from '../../../store'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { usePortfolioClimateData } from '../../../hooks/climateData'
import { EmissionTier, MetricState, CanonicalPeriod, PERIODS, AdaptationView } from '../common'
import wildfireScorePalette, { WildfireScoreBadge } from '../common/fireRisk/utils'
import { useHistory } from 'react-router-dom'
import { LoadingValue } from './PortfolioLoading'
import PortfolioSummaryMetricCard from './PortfolioSummaryMetricCard'
import PortfolioPropertyBreakdownTable, { type PortfolioMetricSpec } from './PortfolioPropertyBreakdownTable'
import PortfolioPropertyMetricMap, {
  type PortfolioMapColorOption,
  type PortfolioMapRadiusOption,
  type NumericMetricSpec,
  type PopupFieldSpec,
} from './PortfolioPropertyMetricMap'
import { useLocations } from '../../../hooks/locations'
import ClimateSectionTitle from '../common/ClimateSectionTitle'
import ClimateMetricCard from '../common/ClimateMetricCard'
import TooltipDataTable from '../common/TooltipDataTable'
import {
  BI_DAYS_LABEL,
  DAMAGE_AAL_LABEL,
  FINANCIAL_BREAKDOWN_TITLE,
  formatDamageTargetLabel,
  formatCompactNumber,
  parseMetricPointY,
  REVENUE_AAL_LABEL,
  TOTAL_AAL_LABEL,
  sumMetricNode,
} from '../common/financialMetrics'
import {
  formatPropertyCoverage,
  meanAggregateValues,
  sumAggregateValues,
  sumAvailableComponentValues,
  type AggregateSummary,
  type ComponentAggregateSummary,
} from '../common/portfolioAggregates'
import { useCurrentUser } from '../../../hooks/users'

type PropertySubset = { mode: 'all' } | { mode: 'selected'; ids: number[] }

type PeriodicMetricMap = {
  values: Record<number, Partial<Record<CanonicalPeriod, number | null>>>
  status: Record<number, MetricState | null>
}

function compactCurrency(value: number | null | undefined, currencySymbol: string) {
  if (value == null || Number.isNaN(value)) return '—'
  return `${currencySymbol}${formatCompactNumber(value, 1)} / year`
}

function fixed1(value: number | null | undefined) {
  if (value == null || Number.isNaN(value)) return '—'
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }).format(value)
}

function formatCoverageFraction(available: number, total: number) {
  return `${available} / ${total}`
}

function buildEntryMap(source: unknown): Record<number, any> {
  const out: Record<number, any> = {}

  if (source && typeof source === 'object') {
    Object.values(source as Record<string, any>).forEach(entry => {
      const propertyId = entry?.property_id
      if (typeof propertyId === 'number') {
        out[propertyId] = entry
      }
    })
  }

  return out
}

function getTierPeriodNode(entry: any, tierKey: string, period: CanonicalPeriod) {
  if (!entry || entry.state?.value !== MetricState.SUCCEEDED || !entry.data) return null
  return entry.data?.data?.[tierKey]?.[period] ?? null
}

function getWildfireDamageAnnualValue(entry: any, tierKey: string, period: CanonicalPeriod, target?: string) {
  const periodNode = getTierPeriodNode(entry, tierKey, period)
  const targetNode = target ? periodNode?.[target] : periodNode
  return sumMetricNode(targetNode)
}

function getWildfireRevenueAnnualValue(entry: any, tierKey: string, period: CanonicalPeriod) {
  return sumMetricNode(getTierPeriodNode(entry, tierKey, period))
}

function renderHelpLines(lines: string[]) {
  return (
    <Stack spacing={0.5}>
      {lines.map(line => (
        <Typography key={line} variant="caption">
          {line}
        </Typography>
      ))}
    </Stack>
  )
}

function renderAggregateTooltip(
  summary: AggregateSummary,
  label: string,
  aggregationText: string,
  extraLines: string[] = [],
) {
  return renderHelpLines([
    `${label}: ${aggregationText}.`,
    `Coverage: ${formatPropertyCoverage(summary.contributors, summary.total)}.`,
    ...extraLines,
  ])
}

function pickScore(entry: any, tierKey: string, period: CanonicalPeriod): number | null {
  if (!entry || entry.state?.value !== MetricState.SUCCEEDED || !entry.data) return null
  return parseMetricPointY(entry.data?.data?.[tierKey]?.[period])
}

function pickAnnualMetric(entry: any, tierKey: string, period: CanonicalPeriod): number | null {
  if (!entry || entry.state?.value !== MetricState.SUCCEEDED || !entry.data) return null
  return sumMetricNode(entry.data?.data?.[tierKey]?.[period])
}

function buildPeriodicMetricMap(
  source: unknown,
  tierKey: string,
  extractor: (entry: any, tierKey: string, period: CanonicalPeriod) => number | null,
): PeriodicMetricMap {
  const values: Record<number, Partial<Record<CanonicalPeriod, number | null>>> = {}
  const status: Record<number, MetricState | null> = {}

  if (source && typeof source === 'object') {
    Object.values(source as Record<string, any>).forEach(entry => {
      const propertyId = entry?.property_id
      if (typeof propertyId !== 'number') return

      status[propertyId] = entry.state?.value ?? null
      const perPeriod: Partial<Record<CanonicalPeriod, number | null>> = {}
      PERIODS.forEach(period => {
        perPeriod[period] = extractor(entry, tierKey, period)
      })
      values[propertyId] = perPeriod
    })
  }

  return { values, status }
}

export default function PortfolioWildfire({
  emissionTier,
  selectedPeriod,
  view,
}: {
  emissionTier: EmissionTier
  selectedPeriod: CanonicalPeriod
  view?: AdaptationView
}) {
  const theme = useTheme()
  const history = useHistory()
  const { groupId } = useSelector((state: RootState) => state.system)
  const { data: locations } = useLocations({ group_id: groupId ?? undefined })
  const numberOfProperties = locations?.length ?? null
  const { currencySymbol } = useCurrentUser()
  const queryParams = useMemo(() => ({ view }), [view])

  const { data: scoreByProperty, isLoading: scoreLoading } = usePortfolioClimateData(
    'wf:score',
    {
      groupId: groupId ?? undefined,
    },
    queryParams,
  )
  const { data: damageAalByProperty, isLoading: damageAalLoading } = usePortfolioClimateData(
    'wf:damage_loss',
    {
      groupId: groupId ?? undefined,
    },
    queryParams,
  )
  const { data: revenueAalByProperty, isLoading: revenueAalLoading } = usePortfolioClimateData(
    'wf:bi_loss',
    {
      groupId: groupId ?? undefined,
    },
    queryParams,
  )
  const { data: biDaysByProperty, isLoading: biDaysLoading } = usePortfolioClimateData(
    'wf:bi_days',
    {
      groupId: groupId ?? undefined,
    },
    queryParams,
  )
  const { data: firesByProperty, isLoading: firesLoading } = usePortfolioClimateData(
    'wf:fires',
    {
      groupId: groupId ?? undefined,
    },
    queryParams,
  )

  const goToProperty = useCallback(
    (propertyId: number) => history.push(siteMap.PropertyClimateAnalytics.getPath(propertyId, 'wf', emissionTier)),
    [emissionTier, history],
  )

  const [propertySubset, setPropertySubset] = useState<PropertySubset>({ mode: 'all' })
  const [mapClearSelectionToken, setMapClearSelectionToken] = useState(0)

  useEffect(() => {
    setPropertySubset({ mode: 'all' })
    setMapClearSelectionToken(value => value + 1)
  }, [groupId])

  const activePropertyIds = useMemo(() => {
    return propertySubset.mode === 'selected' ? propertySubset.ids : undefined
  }, [propertySubset])

  const handleConfirmSelection = useCallback((propertyIds: number[]) => {
    if (!propertyIds.length) {
      setPropertySubset({ mode: 'all' })
      return
    }
    setPropertySubset({ mode: 'selected', ids: propertyIds })
  }, [])

  const handleClearSelection = useCallback(() => {
    setPropertySubset({ mode: 'all' })
  }, [])

  const clearActiveSubset = useCallback(() => {
    setPropertySubset({ mode: 'all' })
    setMapClearSelectionToken(value => value + 1)
  }, [])

  const tierKey = emissionTier as unknown as string
  const damageAalEntryMap = useMemo(() => buildEntryMap(damageAalByProperty), [damageAalByProperty])
  const revenueAalEntryMap = useMemo(() => buildEntryMap(revenueAalByProperty), [revenueAalByProperty])

  const scoreMetric = useMemo(
    () => buildPeriodicMetricMap(scoreByProperty, tierKey, pickScore),
    [scoreByProperty, tierKey],
  )
  const damageAalMetric = useMemo(
    () => buildPeriodicMetricMap(damageAalByProperty, tierKey, pickAnnualMetric),
    [damageAalByProperty, tierKey],
  )
  const revenueAalMetric = useMemo(
    () => buildPeriodicMetricMap(revenueAalByProperty, tierKey, pickAnnualMetric),
    [revenueAalByProperty, tierKey],
  )
  const biDaysMetric = useMemo(
    () => buildPeriodicMetricMap(biDaysByProperty, tierKey, pickAnnualMetric),
    [biDaysByProperty, tierKey],
  )
  const firesMetric = useMemo(
    () => buildPeriodicMetricMap(firesByProperty, tierKey, pickAnnualMetric),
    [firesByProperty, tierKey],
  )

  const totalAalMetric = useMemo<PeriodicMetricMap>(() => {
    const values: PeriodicMetricMap['values'] = {}
    const status: PeriodicMetricMap['status'] = {}
    const propertyIds = new Set<number>([
      ...Object.keys(damageAalMetric.values).map(Number),
      ...Object.keys(revenueAalMetric.values).map(Number),
    ])

    propertyIds.forEach(propertyId => {
      const perPeriod: Partial<Record<CanonicalPeriod, number | null>> = {}
      PERIODS.forEach(period => {
        const damageValue = damageAalMetric.values[propertyId]?.[period] ?? null
        const revenueValue = revenueAalMetric.values[propertyId]?.[period] ?? null
        perPeriod[period] =
          typeof damageValue === 'number' || typeof revenueValue === 'number'
            ? (damageValue ?? 0) + (revenueValue ?? 0)
            : null
      })
      values[propertyId] = perPeriod

      const damageStatus = damageAalMetric.status[propertyId] ?? null
      const revenueStatus = revenueAalMetric.status[propertyId] ?? null
      status[propertyId] =
        damageStatus === MetricState.SUCCEEDED || revenueStatus === MetricState.SUCCEEDED
          ? MetricState.SUCCEEDED
          : damageStatus ?? revenueStatus
    })

    return { values, status }
  }, [damageAalMetric.status, damageAalMetric.values, revenueAalMetric.status, revenueAalMetric.values])

  const selectedPropertyIds = useMemo(() => {
    if (activePropertyIds) return activePropertyIds
    return (locations ?? []).map((location: any) => location.id)
  }, [activePropertyIds, locations])

  const kpis = useMemo(() => {
    return PERIODS.reduce(
      (out, period) => {
        const scoreSummary = meanAggregateValues(
          selectedPropertyIds,
          propertyId => scoreMetric.values[propertyId]?.[period] ?? null,
        )
        const damageAalSummary = sumAggregateValues(
          selectedPropertyIds,
          propertyId => damageAalMetric.values[propertyId]?.[period] ?? null,
        )
        const revenueAalSummary = sumAggregateValues(
          selectedPropertyIds,
          propertyId => revenueAalMetric.values[propertyId]?.[period] ?? null,
        )
        const biDaysSummary = meanAggregateValues(
          selectedPropertyIds,
          propertyId => biDaysMetric.values[propertyId]?.[period] ?? null,
        )
        const totalAalSummary = sumAvailableComponentValues(selectedPropertyIds, {
          structure: propertyId =>
            getWildfireDamageAnnualValue(damageAalEntryMap[propertyId], tierKey, period, 'structure'),
          contents: propertyId =>
            getWildfireDamageAnnualValue(damageAalEntryMap[propertyId], tierKey, period, 'contents'),
          revenue: propertyId => getWildfireRevenueAnnualValue(revenueAalEntryMap[propertyId], tierKey, period),
        })

        out[period] = {
          scoreSummary,
          highCount: selectedPropertyIds.filter(propertyId => {
            const value = scoreMetric.values[propertyId]?.[period]
            return typeof value === 'number' && value >= 4
          }).length,
          totalAalSummary,
          damageAalSummary,
          revenueAalSummary,
          biDaysSummary,
        }

        return out
      },
      {} as Record<
        CanonicalPeriod,
        {
          scoreSummary: AggregateSummary
          highCount: number
          totalAalSummary: ComponentAggregateSummary
          damageAalSummary: AggregateSummary
          revenueAalSummary: AggregateSummary
          biDaysSummary: AggregateSummary
        }
      >,
    )
  }, [
    biDaysMetric.values,
    damageAalEntryMap,
    damageAalMetric.values,
    revenueAalEntryMap,
    revenueAalMetric.values,
    scoreMetric.values,
    selectedPropertyIds,
    tierKey,
  ])

  const selectedKpi = kpis[selectedPeriod]
  const totalAalHelpText = useMemo(
    () =>
      renderHelpLines([
        `${TOTAL_AAL_LABEL} = Structure Damage AAL + Contents Damage AAL + BI AAL.`,
        `Coverage: ${formatPropertyCoverage(
          selectedKpi.totalAalSummary.contributors,
          selectedKpi.totalAalSummary.total,
        )}.`,
        `Structure available for ${formatPropertyCoverage(
          selectedKpi.totalAalSummary.componentContributors.structure ?? 0,
          selectedKpi.totalAalSummary.total,
        )}.`,
        `Contents available for ${formatPropertyCoverage(
          selectedKpi.totalAalSummary.componentContributors.contents ?? 0,
          selectedKpi.totalAalSummary.total,
        )}.`,
        `Revenue available for ${formatPropertyCoverage(
          selectedKpi.totalAalSummary.componentContributors.revenue ?? 0,
          selectedKpi.totalAalSummary.total,
        )}.`,
      ]),
    [selectedKpi.totalAalSummary],
  )
  const riskRatingHelpText = useMemo(
    () =>
      renderAggregateTooltip(selectedKpi.scoreSummary, 'Average Risk Rating', 'Average across available risk ratings'),
    [selectedKpi.scoreSummary],
  )
  const highRiskHelpText = useMemo(
    () =>
      renderHelpLines([
        'Count of selected properties rated High or Very High.',
        `Score coverage: ${formatPropertyCoverage(
          selectedKpi.scoreSummary.contributors,
          selectedKpi.scoreSummary.total,
        )}.`,
      ]),
    [selectedKpi.highCount, selectedKpi.scoreSummary],
  )
  const damageAalHelpText = useMemo(
    () =>
      renderAggregateTooltip(
        selectedKpi.damageAalSummary,
        DAMAGE_AAL_LABEL,
        'Summed across structure and contents damage where available',
      ),
    [selectedKpi.damageAalSummary],
  )
  const revenueAalHelpText = useMemo(
    () =>
      renderAggregateTooltip(
        selectedKpi.revenueAalSummary,
        REVENUE_AAL_LABEL,
        'Summed across available revenue-loss values',
      ),
    [selectedKpi.revenueAalSummary],
  )
  const biDaysHelpText = useMemo(
    () =>
      renderAggregateTooltip(
        selectedKpi.biDaysSummary,
        BI_DAYS_LABEL,
        'Average across available business interruption day values',
      ),
    [selectedKpi.biDaysSummary],
  )

  const getDamageAssetSummary = useCallback(
    (period: CanonicalPeriod, target: 'structure' | 'contents') =>
      sumAggregateValues(selectedPropertyIds, propertyId =>
        getWildfireDamageAnnualValue(damageAalEntryMap[propertyId], tierKey, period, target),
      ),
    [damageAalEntryMap, selectedPropertyIds, tierKey],
  )

  const damageAssetBreakdownHasData = useMemo(
    () =>
      ['structure', 'contents'].some(
        target => getDamageAssetSummary(selectedPeriod, target as 'structure' | 'contents').value != null,
      ),
    [getDamageAssetSummary, selectedPeriod],
  )

  const damageBreakdownHelpText = useMemo(
    () => (
      <TooltipDataTable
        title={`Properties contributing data (${selectedPropertyIds.length} selected)`}
        columns={['Properties']}
        rows={(['structure', 'contents'] as const).map(target => {
          const summary = getDamageAssetSummary(selectedPeriod, target)
          return {
            label: formatDamageTargetLabel(target),
            values: [formatCoverageFraction(summary.contributors, summary.total)],
          }
        })}
      />
    ),
    [getDamageAssetSummary, selectedPeriod, selectedPropertyIds.length],
  )

  const tableMetrics = useMemo<PortfolioMetricSpec[]>(
    () => [
      {
        kind: 'score',
        key: 'score',
        label: 'Risk Rating',
        perPeriod: true,
        width: 150,
        loading: scoreLoading,
        getValue: (propertyId, period) => scoreMetric.values[propertyId]?.[period ?? CanonicalPeriod.LONG_TERM] ?? null,
        renderScore: value => <WildfireScoreBadge score={value} size="dense" />,
        status: {
          key: 'statusScore',
          label: 'Status (Risk Rating)',
          hiddenByDefault: true,
          loading: scoreLoading,
          getValue: propertyId => scoreMetric.status[propertyId] ?? null,
        },
      },
      {
        kind: 'currency',
        key: 'total_aal',
        label: TOTAL_AAL_LABEL,
        perPeriod: true,
        width: 170,
        loading: damageAalLoading || revenueAalLoading,
        suffix: '/ year',
        getValue: (propertyId, period) =>
          totalAalMetric.values[propertyId]?.[period ?? CanonicalPeriod.LONG_TERM] ?? null,
        status: {
          key: 'statusTotalAal',
          label: `Status (${TOTAL_AAL_LABEL})`,
          hiddenByDefault: true,
          loading: damageAalLoading || revenueAalLoading,
          getValue: propertyId => totalAalMetric.status[propertyId] ?? null,
        },
      },
      {
        kind: 'currency',
        key: 'damage_aal',
        label: DAMAGE_AAL_LABEL,
        perPeriod: true,
        width: 170,
        loading: damageAalLoading,
        suffix: '/ year',
        getValue: (propertyId, period) =>
          damageAalMetric.values[propertyId]?.[period ?? CanonicalPeriod.LONG_TERM] ?? null,
        status: {
          key: 'statusDamageAal',
          label: `Status (${DAMAGE_AAL_LABEL})`,
          hiddenByDefault: true,
          loading: damageAalLoading,
          getValue: propertyId => damageAalMetric.status[propertyId] ?? null,
        },
      },
      {
        kind: 'currency',
        key: 'revenue_aal',
        label: REVENUE_AAL_LABEL,
        perPeriod: true,
        width: 170,
        loading: revenueAalLoading,
        suffix: '/ year',
        getValue: (propertyId, period) =>
          revenueAalMetric.values[propertyId]?.[period ?? CanonicalPeriod.LONG_TERM] ?? null,
        status: {
          key: 'statusRevenueAal',
          label: `Status (${REVENUE_AAL_LABEL})`,
          hiddenByDefault: true,
          loading: revenueAalLoading,
          getValue: propertyId => revenueAalMetric.status[propertyId] ?? null,
        },
      },
      {
        kind: 'number',
        key: 'bi_days',
        label: BI_DAYS_LABEL,
        perPeriod: true,
        width: 160,
        loading: biDaysLoading,
        format: 'fixed1',
        suffix: 'days/year',
        getValue: (propertyId, period) =>
          biDaysMetric.values[propertyId]?.[period ?? CanonicalPeriod.LONG_TERM] ?? null,
        status: {
          key: 'statusBiDays',
          label: `Status (${BI_DAYS_LABEL})`,
          hiddenByDefault: true,
          loading: biDaysLoading,
          getValue: propertyId => biDaysMetric.status[propertyId] ?? null,
        },
      },
    ],
    [
      biDaysLoading,
      biDaysMetric.status,
      biDaysMetric.values,
      damageAalLoading,
      damageAalMetric.status,
      damageAalMetric.values,
      revenueAalLoading,
      revenueAalMetric.status,
      revenueAalMetric.values,
      scoreLoading,
      scoreMetric.status,
      scoreMetric.values,
      totalAalMetric.status,
      totalAalMetric.values,
    ],
  )

  const scoreNumericMetric = useMemo<NumericMetricSpec>(
    () => ({
      key: 'score',
      label: 'Risk Rating',
      perPeriod: true,
      loading: scoreLoading,
      getValue: (propertyId, period) => scoreMetric.values[propertyId]?.[period] ?? null,
      renderValue: value => <WildfireScoreBadge score={value} size="dense" />,
    }),
    [scoreLoading, scoreMetric.values],
  )

  const totalAalNumericMetric = useMemo<NumericMetricSpec>(
    () => ({
      key: 'total_aal',
      label: TOTAL_AAL_LABEL,
      perPeriod: true,
      loading: damageAalLoading || revenueAalLoading,
      formatKind: 'currency',
      suffix: '/ year',
      getValue: (propertyId, period) => totalAalMetric.values[propertyId]?.[period] ?? null,
    }),
    [damageAalLoading, revenueAalLoading, totalAalMetric.values],
  )

  const damageAalNumericMetric = useMemo<NumericMetricSpec>(
    () => ({
      key: 'damage_aal',
      label: DAMAGE_AAL_LABEL,
      perPeriod: true,
      loading: damageAalLoading,
      formatKind: 'currency',
      suffix: '/ year',
      getValue: (propertyId, period) => damageAalMetric.values[propertyId]?.[period] ?? null,
    }),
    [damageAalLoading, damageAalMetric.values],
  )

  const revenueAalNumericMetric = useMemo<NumericMetricSpec>(
    () => ({
      key: 'revenue_aal',
      label: REVENUE_AAL_LABEL,
      perPeriod: true,
      loading: revenueAalLoading,
      formatKind: 'currency',
      suffix: '/ year',
      getValue: (propertyId, period) => revenueAalMetric.values[propertyId]?.[period] ?? null,
    }),
    [revenueAalLoading, revenueAalMetric.values],
  )

  const mapColorOptions = useMemo<PortfolioMapColorOption[]>(
    () => [
      {
        key: 'score',
        label: 'Risk Rating',
        type: 'discrete',
        metric: scoreNumericMetric,
        palette: wildfireScorePalette,
        labels: {
          1: 'Negligible',
          2: 'Low',
          3: 'Moderate',
          4: 'High',
          5: 'Very High',
        },
        normalize: value => Math.min(Math.max(Math.round(value), 1), 5),
      },
      {
        key: 'total_aal',
        label: TOTAL_AAL_LABEL,
        type: 'continuous',
        metric: totalAalNumericMetric,
        domain: { type: 'auto', clampPercentiles: [5, 95] },
        gradient: {
          stops: [
            { offset: 0, color: '#2ecc71' },
            { offset: 0.5, color: '#f1c40f' },
            { offset: 1, color: '#e74c3c' },
          ],
        },
      },
      {
        key: 'damage_aal',
        label: DAMAGE_AAL_LABEL,
        type: 'continuous',
        metric: damageAalNumericMetric,
        domain: { type: 'auto', clampPercentiles: [5, 95] },
        gradient: {
          stops: [
            { offset: 0, color: '#2ecc71' },
            { offset: 0.5, color: '#f1c40f' },
            { offset: 1, color: '#e74c3c' },
          ],
        },
      },
      {
        key: 'revenue_aal',
        label: REVENUE_AAL_LABEL,
        type: 'continuous',
        metric: revenueAalNumericMetric,
        domain: { type: 'auto', clampPercentiles: [5, 95] },
        gradient: {
          stops: [
            { offset: 0, color: '#2ecc71' },
            { offset: 0.5, color: '#f1c40f' },
            { offset: 1, color: '#e74c3c' },
          ],
        },
      },
    ],
    [damageAalNumericMetric, revenueAalNumericMetric, scoreNumericMetric, totalAalNumericMetric],
  )

  const mapRadiusOptions = useMemo<PortfolioMapRadiusOption[]>(
    () => [
      {
        key: 'total_aal',
        label: TOTAL_AAL_LABEL,
        type: 'scaled',
        metric: totalAalNumericMetric,
        scale: 'sqrt',
        domain: { type: 'auto', clampPercentiles: [5, 95] },
        rangePx: { min: 6, max: 24 },
      },
      {
        key: 'damage_aal',
        label: DAMAGE_AAL_LABEL,
        type: 'scaled',
        metric: damageAalNumericMetric,
        scale: 'sqrt',
        domain: { type: 'auto', clampPercentiles: [5, 95] },
        rangePx: { min: 6, max: 24 },
      },
      {
        key: 'revenue_aal',
        label: REVENUE_AAL_LABEL,
        type: 'scaled',
        metric: revenueAalNumericMetric,
        scale: 'sqrt',
        domain: { type: 'auto', clampPercentiles: [5, 95] },
        rangePx: { min: 6, max: 24 },
      },
    ],
    [damageAalNumericMetric, revenueAalNumericMetric, totalAalNumericMetric],
  )

  const mapPopupFields = useMemo<PopupFieldSpec[]>(
    () => [
      {
        key: 'total_aal',
        label: TOTAL_AAL_LABEL,
        perPeriod: true,
        loading: damageAalLoading || revenueAalLoading,
        formatKind: 'currency',
        suffix: '/ year',
        getValue: (propertyId, period) => totalAalMetric.values[propertyId]?.[period] ?? null,
      },
      {
        key: 'damage_aal',
        label: DAMAGE_AAL_LABEL,
        perPeriod: true,
        loading: damageAalLoading,
        formatKind: 'currency',
        suffix: '/ year',
        getValue: (propertyId, period) => damageAalMetric.values[propertyId]?.[period] ?? null,
      },
      {
        key: 'revenue_aal',
        label: REVENUE_AAL_LABEL,
        perPeriod: true,
        loading: revenueAalLoading,
        formatKind: 'currency',
        suffix: '/ year',
        getValue: (propertyId, period) => revenueAalMetric.values[propertyId]?.[period] ?? null,
      },
      {
        key: 'bi_days',
        label: BI_DAYS_LABEL,
        perPeriod: true,
        loading: biDaysLoading,
        decimals: 1,
        suffix: 'days/year',
        getValue: (propertyId, period) => biDaysMetric.values[propertyId]?.[period] ?? null,
      },
      {
        key: 'fires',
        label: 'Estimated Fires / year',
        perPeriod: true,
        loading: firesLoading,
        decimals: 1,
        getValue: (propertyId, period) => firesMetric.values[propertyId]?.[period] ?? null,
      },
    ],
    [
      biDaysLoading,
      biDaysMetric.values,
      damageAalLoading,
      damageAalMetric.values,
      firesLoading,
      firesMetric.values,
      revenueAalLoading,
      revenueAalMetric.values,
      totalAalMetric.values,
    ],
  )

  const kpiMotionKey = `${emissionTier}-${selectedPeriod}`

  return (
    <Stack alignItems="center" pb={6}>
      <Stack sx={{ maxWidth: theme.breakpoints.values?.lg, width: '100%' }} spacing={2}>
        {activePropertyIds && (
          <Alert
            severity="info"
            sx={{ borderRadius: 2 }}
            action={
              <Button color="inherit" size="small" onClick={clearActiveSubset}>
                Clear selection
              </Button>
            }
          >
            <Typography variant="body2" fontWeight={800}>
              Property filter
            </Typography>
            <Typography variant="body2">
              Showing data for a subset of properties ({activePropertyIds.length.toLocaleString()} out of{' '}
              {numberOfProperties?.toLocaleString() ?? '—'}).
            </Typography>
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <PortfolioSummaryMetricCard
              title="Average Risk Rating"
              helpText={riskRatingHelpText}
              loading={scoreLoading}
              transitionKey={kpiMotionKey}
            >
              <LoadingValue loading={scoreLoading} variant="rounded" width={72} height={20}>
                <WildfireScoreBadge score={selectedKpi.scoreSummary.value} size="dense" />
              </LoadingValue>
            </PortfolioSummaryMetricCard>
          </Grid>

          <Grid item xs={12} md={4}>
            <PortfolioSummaryMetricCard
              title="High / Very High Risk Ratings"
              helpText={highRiskHelpText}
              loading={scoreLoading}
              transitionKey={kpiMotionKey}
            >
              <LoadingValue loading={scoreLoading} variant="rounded" width={60} height={24}>
                <Chip label={selectedKpi.highCount} />
              </LoadingValue>
            </PortfolioSummaryMetricCard>
          </Grid>

          <Grid item xs={12} md={4}>
            <PortfolioSummaryMetricCard
              title={TOTAL_AAL_LABEL}
              helpText={totalAalHelpText}
              loading={damageAalLoading || revenueAalLoading}
              transitionKey={kpiMotionKey}
            >
              <LoadingValue loading={damageAalLoading || revenueAalLoading} variant="rounded" width={140} height={24}>
                <Chip label={compactCurrency(selectedKpi.totalAalSummary.value, currencySymbol)} />
              </LoadingValue>
            </PortfolioSummaryMetricCard>
          </Grid>
        </Grid>

        <Card variant="outlined">
          <CardContent>
            <Stack spacing={2}>
              <ClimateSectionTitle
                title={FINANCIAL_BREAKDOWN_TITLE}
                subtitle="Damage and revenue components of annualised losses across the selected properties"
              />

              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <PortfolioSummaryMetricCard
                    title={DAMAGE_AAL_LABEL}
                    helpText={damageAalHelpText}
                    loading={damageAalLoading}
                    transitionKey={kpiMotionKey}
                  >
                    <LoadingValue loading={damageAalLoading} variant="rounded" width={140} height={24}>
                      <Chip label={compactCurrency(selectedKpi.damageAalSummary.value, currencySymbol)} />
                    </LoadingValue>
                  </PortfolioSummaryMetricCard>
                </Grid>

                <Grid item xs={12} md={4}>
                  <PortfolioSummaryMetricCard
                    title={REVENUE_AAL_LABEL}
                    helpText={revenueAalHelpText}
                    loading={revenueAalLoading}
                    transitionKey={kpiMotionKey}
                  >
                    <LoadingValue loading={revenueAalLoading} variant="rounded" width={140} height={24}>
                      <Chip label={compactCurrency(selectedKpi.revenueAalSummary.value, currencySymbol)} />
                    </LoadingValue>
                  </PortfolioSummaryMetricCard>
                </Grid>

                <Grid item xs={12} md={4}>
                  <PortfolioSummaryMetricCard
                    title={BI_DAYS_LABEL}
                    helpText={biDaysHelpText}
                    loading={biDaysLoading}
                    transitionKey={kpiMotionKey}
                  >
                    <LoadingValue loading={biDaysLoading} variant="rounded" width={120} height={24}>
                      <Chip
                        label={
                          selectedKpi.biDaysSummary.value != null
                            ? `${fixed1(selectedKpi.biDaysSummary.value)} days/year`
                            : '—'
                        }
                      />
                    </LoadingValue>
                  </PortfolioSummaryMetricCard>
                </Grid>
              </Grid>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <ClimateMetricCard
                    title="Damage AAL Breakdown"
                    subtitle="Structure and contents split for annualised wildfire damage"
                    helpText={damageBreakdownHelpText}
                    loading={damageAalLoading}
                    transitionKey={kpiMotionKey}
                    contentSx={{ height: '100%' }}
                  >
                    {!damageAssetBreakdownHasData ? (
                      <Typography variant="body2" color="text.secondary">
                        No damage breakdown is available for this selection.
                      </Typography>
                    ) : (
                      <Table size="small">
                        <TableBody>
                          {(['structure', 'contents'] as const).map(target => {
                            const summary = getDamageAssetSummary(selectedPeriod, target)
                            return (
                              <TableRow key={target}>
                                <TableCell component="th" scope="row">
                                  {formatDamageTargetLabel(target)}
                                </TableCell>
                                <TableCell align="right">
                                  <Tooltip
                                    title={renderHelpLines([
                                      'Aggregation: sum across contributing properties.',
                                      `Coverage: ${formatPropertyCoverage(summary.contributors, summary.total)}.`,
                                    ])}
                                    arrow
                                    enterTouchDelay={0}
                                  >
                                    <Box component="span">{compactCurrency(summary.value, currencySymbol)}</Box>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            )
                          })}
                        </TableBody>
                      </Table>
                    )}
                  </ClimateMetricCard>
                </Grid>
              </Grid>
            </Stack>
          </CardContent>
        </Card>

        <PortfolioPropertyMetricMap
          title="Portfolio Map"
          height={600}
          selectedPeriod={selectedPeriod}
          colorOptions={mapColorOptions}
          radiusOptions={mapRadiusOptions}
          defaultColorKey="score"
          defaultRadiusKey="default"
          popupFields={mapPopupFields}
          onViewProperty={goToProperty}
          onConfirmSelection={handleConfirmSelection}
          onClearSelection={handleClearSelection}
          clearSelectionToken={mapClearSelectionToken}
        />

        <PortfolioPropertyBreakdownTable
          metrics={tableMetrics}
          selectedPeriod={selectedPeriod}
          filterPropertyIds={activePropertyIds}
          onViewProperty={goToProperty}
          height={680}
          initialSort={{ metricKey: 'score', period: selectedPeriod, sort: 'desc' }}
        />
      </Stack>
    </Stack>
  )
}
